// CertificateVerifyPage — публичная страница верификации сертификата.
// ─────────────────────────────────────────────────────────────────────
// Открывается при сканировании QR-кода на PDF: /verify/:token
// Никакого auth — кто угодно (работодатель, ВУЗ) может проверить.
// Показывает: подлинный/отозван/не найден, имя, курс, дату, номер.
//
// НЕ показывает: email, userId, courseId, instructorId — это personal data
// и третьи лица не должны иметь к ним доступ.

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Award,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  Calendar,
  User as UserIcon,
  BookOpen,
  Hash,
} from 'lucide-react';
import { useVerifyCertificateQuery } from '../../api/apiSlice';
import logo from '../../img/logo.png';

const CertificateVerifyPage = () => {
  const { token } = useParams();
  const { t } = useTranslation();
  const { data, isLoading, isError } = useVerifyCertificateQuery(token, {
    skip: !token,
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-amber-50 dark:from-slate-950 dark:via-slate-900 dark:to-amber-950/20 flex flex-col">
      {/* Brand header — без navigation, чтобы фокус был на верификации */}
      <header className="border-b border-slate-200/60 dark:border-slate-800/60 bg-white/70 dark:bg-slate-900/70 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
          <img
                        src={logo}
                        alt="Uaqyt"
                        className="h-[100px] w-[150px] object-contain"
                      />
          
          </Link>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {t('certificates.verifyTitle')}
          </span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl">
          {isLoading ? (
            <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl p-12 text-center">
              <Loader2 className="w-10 h-10 mx-auto text-indigo-600 animate-spin" />
              <p className="mt-4 text-sm text-slate-500">Проверяем…</p>
            </div>
          ) : isError || !data ? (
            <VerifyResultCard
              icon={XCircle}
              tone="red"
              title={t('certificates.verifyNotFound')}
              subtitle={t('certificates.verifyNotFoundHint')}
            />
          ) : data.revoked ? (
            <VerifyResultCard
              icon={AlertTriangle}
              tone="red"
              title={t('certificates.verifyRevoked')}
              subtitle={data.revokedReason || ''}
            >
              <CertificateDetails cert={data.certificate} t={t} />
            </VerifyResultCard>
          ) : data.valid ? (
            <VerifyResultCard
              icon={CheckCircle}
              tone="emerald"
              title={t('certificates.verifyAuthentic')}
            >
              <CertificateDetails cert={data.certificate} t={t} />
            </VerifyResultCard>
          ) : (
            <VerifyResultCard
              icon={XCircle}
              tone="red"
              title={t('certificates.verifyNotFound')}
              subtitle={t('certificates.verifyNotFoundHint')}
            />
          )}
        </div>
      </main>

      <footer className="border-t border-slate-200/60 dark:border-slate-800/60 bg-white/50 dark:bg-slate-900/50">
        <div className="max-w-3xl mx-auto px-4 py-4 text-center text-xs text-slate-500 dark:text-slate-400">
          {t('certificates.verifyPoweredBy')} ·{' '}
          <Link to="/" className="hover:text-indigo-600 dark:hover:text-indigo-400 underline-offset-2 hover:underline">
            uaqyt.com
          </Link>
        </div>
      </footer>
    </div>
  );
};

const VerifyResultCard = ({ icon: Icon, tone, title, subtitle, children }) => {
  const palette = {
    emerald: {
      ring: 'ring-emerald-200 dark:ring-emerald-900/40',
      iconBg: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
      titleColor: 'text-emerald-700 dark:text-emerald-300',
    },
    red: {
      ring: 'ring-red-200 dark:ring-red-900/40',
      iconBg: 'bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400',
      titleColor: 'text-red-700 dark:text-red-300',
    },
  }[tone];
  return (
    <div className={`rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl ring-4 ${palette.ring} overflow-hidden`}>
      <div className="p-8 sm:p-10 text-center">
        <div className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl ${palette.iconBg}`}>
          <Icon className="w-8 h-8" />
        </div>
        <h1 className={`text-2xl sm:text-3xl font-bold ${palette.titleColor}`}>
          {title}
        </h1>
        {subtitle && (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            {subtitle}
          </p>
        )}
      </div>
      {children && (
        <div className="border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/30 p-6 sm:p-8">
          {children}
        </div>
      )}
    </div>
  );
};

const CertificateDetails = ({ cert, t }) => {
  if (!cert) return null;
  const rows = [
    { icon: UserIcon, label: t('certificates.verifyIssuedTo'), value: cert.userName },
    { icon: BookOpen, label: t('certificates.verifyCourseCompleted'), value: `«${cert.courseTitle}»` },
    cert.instructorName && {
      icon: Award,
      label: t('certificates.instructor'),
      value: cert.instructorName,
    },
    {
      icon: Calendar,
      label: t('certificates.issuedOn'),
      value: new Date(cert.issuedAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    },
    { icon: Hash, label: t('certificates.number'), value: cert.certificateNumber, mono: true },
  ].filter(Boolean);

  return (
    <dl className="space-y-3">
      {rows.map((r, i) => {
        const Icon = r.icon;
        return (
          <div key={i} className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-indigo-600 dark:text-indigo-400">
              <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <dt className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {r.label}
              </dt>
              <dd className={`mt-0.5 font-semibold text-slate-900 dark:text-white break-words ${r.mono ? 'font-mono text-sm' : ''}`}>
                {r.value}
              </dd>
            </div>
          </div>
        );
      })}
    </dl>
  );
};

export default CertificateVerifyPage;
